import {
  EDITORIAL_CATEGORY_IDS_FOR_ANY_COUNTRY,
  EDITORIAL_CATEGORY_MEMBER_COUNTRY_IDS,
  GLOBAL_SCOPE_ID,
  PUBLICATION_REGIONS,
  getCountryName,
} from "@legal-500/discovery/data";

export type Jurisdiction = { id: string; name: string };

export type SubscriptionRegion = {
  scopeId: string;
  name: string;
  computed: boolean;
  jurisdictions: Jurisdiction[];
};

export type EditorialCategory = {
  id: string;
  name: string;
  unlockedByAny: boolean;
  unlockedBy: Jurisdiction[];
};

export type SearchMatch = Jurisdiction & {
  openedBy: string;
  inAnyRegion: boolean;
  categories: Jurisdiction[];
  unlockedByAny: boolean;
  unlockedBy: Jurisdiction[];
};

const NUMERIC_ID = /^\d+$/;

export function isNumericId(term: string): boolean {
  return NUMERIC_ID.test(term);
}

function jurisdiction(id: string): Jurisdiction {
  return { id, name: getCountryName(id)?.trim() ?? id };
}

function unique(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

const RECOGNISED_JURISDICTIONS = unique(
  PUBLICATION_REGIONS.flatMap(region => region.memberCountryIds),
).map(jurisdiction);

export const SUBSCRIPTION_REGIONS: SubscriptionRegion[] = [
  ...PUBLICATION_REGIONS.flatMap(region =>
    region.entitlementScopeId === null
      ? []
      : [
          {
            scopeId: region.entitlementScopeId,
            name: region.name,
            computed: false,
            jurisdictions: unique(region.memberCountryIds).map(jurisdiction),
          },
        ],
  ),
  {
    scopeId: GLOBAL_SCOPE_ID,
    name: "Global",
    computed: true,
    jurisdictions: RECOGNISED_JURISDICTIONS,
  },
];

export const EDITORIAL_CATEGORIES: EditorialCategory[] = [
  ...Object.entries(EDITORIAL_CATEGORY_MEMBER_COUNTRY_IDS).map(([id, memberIds]) => ({
    ...jurisdiction(id),
    unlockedByAny: false,
    unlockedBy: memberIds.map(memberId => jurisdiction(String(memberId))),
  })),
  ...EDITORIAL_CATEGORY_IDS_FOR_ANY_COUNTRY.map(id => ({
    ...jurisdiction(String(id)),
    unlockedByAny: true,
    unlockedBy: [],
  })),
];

function normalise(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function toMatch(found: Jurisdiction): SearchMatch {
  const regions = SUBSCRIPTION_REGIONS.filter(
    region => !region.computed && region.jurisdictions.some(member => member.id === found.id),
  );
  const ownCategory = EDITORIAL_CATEGORIES.find(category => category.id === found.id);
  return {
    ...found,
    openedBy: [
      ...regions.map(region => `Region ${region.name}`),
      `Country ${found.name}`,
      "Global",
    ].join(" or "),
    inAnyRegion: regions.length > 0,
    categories: EDITORIAL_CATEGORIES.filter(
      category =>
        category.id !== found.id &&
        (category.unlockedByAny || category.unlockedBy.some(member => member.id === found.id)),
    ),
    unlockedByAny: ownCategory?.unlockedByAny ?? false,
    unlockedBy: ownCategory?.unlockedBy ?? [],
  };
}

export function searchJurisdictions(query: string): SearchMatch[] {
  const term = query.trim();
  if (term === "") return [];
  if (isNumericId(term)) {
    return RECOGNISED_JURISDICTIONS.filter(found => found.id === term).map(toMatch);
  }
  const needle = normalise(term);
  return RECOGNISED_JURISDICTIONS.filter(found => normalise(found.name).includes(needle)).map(
    toMatch,
  );
}
